import { useState } from "react";
import { toast } from "sonner";
import Navigation from "./Navigation";
import Footer from "./Footer";
import { Reveal } from "./motion-helpers";
import { usePageMeta } from "./page-meta";
import { WHATSAPP_URL, WHATSAPP_MSG } from "./constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  { value: "wedding", label: "Wedding / Celebration" },
  { value: "corporate", label: "Corporate / Offsite" },
  { value: "festival", label: "Festival / Pop-up" },
  { value: "cafe-retail", label: "Café / Retail Stocking" },
  { value: "private-party", label: "Private Party" },
  { value: "other", label: "Something Else" },
];

const QUANTITIES = [
  { value: "under-50", label: "Under 50" },
  { value: "50-100", label: "50 – 100" },
  { value: "100-250", label: "100 – 250" },
  { value: "250-500", label: "250 – 500" },
  { value: "500-plus", label: "500+" },
];

const CONTACT_PREFS = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone Call" },
];

const INITIAL = {
  name: "",
  company: "",
  email: "",
  phone: "",
  city: "",
  event_type: "wedding",
  event_date: "",
  quantity: "50-100",
  details: "",
  contact_pref: "whatsapp",
};

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm text-[#F5EFE6] placeholder-[#8C8177] outline-none transition-colors focus:border-[#D9A441]/60";

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label
      htmlFor={htmlFor}
      className="mb-2 block text-[11px] uppercase tracking-[0.28em] text-[#8C8177]"
    >
      {label}
    </label>
    {children}
  </div>
);

export const MomentsPage = ({ scrollTo }) => {
  usePageMeta(
    "For Moments — Events, Corporate & Bulk Orders | DUNC Kombucha",
    "Bring DUNC sparkling kombucha to your wedding, corporate event, festival or café. Tell us about your moment and we'll bring the cans."
  );

  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Something went wrong. Please try again.");
      }
      setDone(true);
      toast.success("Enquiry sent. We'll reach out shortly.");
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Navigation scrollTo={scrollTo} />
      <main className="relative min-h-screen overflow-hidden" data-testid="moments-page">
        <div className="relative z-10 px-6 pt-36 pb-32 md:pt-44">
          <div className="mx-auto max-w-3xl">
            <Reveal>
              <p className="mb-6 text-xs uppercase tracking-[0.4em] text-[#D9A441]">
                For Moments
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.02] text-[#F5EFE6]">
                Let's make your moment <span className="text-[#D9A441]">sparkle.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-[#CFC6BC]">
                Weddings, offsites, festivals, café counters, private parties —
                tell us what you're planning and we'll get back within a day.
              </p>
            </Reveal>

            {done ? (
              <Reveal delay={0.1}>
                <div
                  className="mt-14 rounded-3xl border border-[#D9A441]/30 bg-white/[0.02] p-8 md:p-10"
                  data-testid="moments-success"
                >
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-[#F5EFE6]">
                    Enquiry received.
                  </h2>
                  <p className="mt-4 text-sm leading-relaxed text-[#CFC6BC]">
                    The DUNC team will reach out on your preferred channel within
                    a day. Want to speed things up?
                  </p>
                  <a
                    href={WHATSAPP_URL(WHATSAPP_MSG.moments)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="moments-success-whatsapp"
                    className="mt-6 inline-flex items-center gap-3 rounded-full bg-[#D9A441] px-8 py-4 text-[12px] font-medium uppercase tracking-[0.28em] text-[#181310] transition-colors hover:bg-[#E7B35A]"
                  >
                    Continue on WhatsApp
                  </a>
                </div>
              </Reveal>
            ) : (
              <Reveal delay={0.15}>
                <form
                  onSubmit={submit}
                  className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-5"
                  data-testid="moments-form"
                >
                  <Field label="Your Name *" htmlFor="moments-name">
                    <input id="moments-name" data-testid="moments-name" required maxLength={80}
                      value={form.name} onChange={set("name")} placeholder="Asha Rao" className={inputCls} />
                  </Field>
                  <Field label="Company / Organisation" htmlFor="moments-company">
                    <input id="moments-company" data-testid="moments-company" maxLength={100}
                      value={form.company} onChange={set("company")} placeholder="Optional" className={inputCls} />
                  </Field>
                  <Field label="Email *" htmlFor="moments-email">
                    <input id="moments-email" data-testid="moments-email" type="email" required
                      value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                  </Field>
                  <Field label="Phone *" htmlFor="moments-phone">
                    <input id="moments-phone" data-testid="moments-phone" type="tel" required maxLength={20}
                      value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
                  </Field>
                  <Field label="City *" htmlFor="moments-city">
                    <input id="moments-city" data-testid="moments-city" required maxLength={60}
                      value={form.city} onChange={set("city")} placeholder="Bangalore" className={inputCls} />
                  </Field>
                  <Field label="Event Date" htmlFor="moments-date">
                    <input id="moments-date" data-testid="moments-date" type="date"
                      value={form.event_date} onChange={set("event_date")} className={inputCls} />
                  </Field>
                  <Field label="Event Type *" htmlFor="moments-type">
                    <select id="moments-type" data-testid="moments-type" required
                      value={form.event_type} onChange={set("event_type")} className={inputCls}>
                      {EVENT_TYPES.map((t) => (
                        <option key={t.value} value={t.value} className="bg-[#1a1512]">{t.label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Approx. Quantity *" htmlFor="moments-quantity">
                    <select id="moments-quantity" data-testid="moments-quantity" required
                      value={form.quantity} onChange={set("quantity")} className={inputCls}>
                      {QUANTITIES.map((q) => (
                        <option key={q.value} value={q.value} className="bg-[#1a1512]">{q.label}</option>
                      ))}
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Tell us about the moment" htmlFor="moments-details">
                      <textarea id="moments-details" data-testid="moments-details" rows={4} maxLength={1500}
                        value={form.details} onChange={set("details")}
                        placeholder="Guest count, vibe, dates, flavours you're eyeing..."
                        className={inputCls} />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="mb-3 block text-[11px] uppercase tracking-[0.28em] text-[#8C8177]">
                      Preferred way to connect *
                    </span>
                    <div className="flex flex-wrap gap-3" data-testid="moments-contact-pref">
                      {CONTACT_PREFS.map((c) => (
                        <label
                          key={c.value}
                          className={`cursor-pointer rounded-full border px-5 py-2.5 text-[12px] uppercase tracking-[0.2em] transition-colors ${
                            form.contact_pref === c.value
                              ? "border-[#D9A441] bg-[#D9A441]/10 text-[#D9A441]"
                              : "border-white/15 text-[#CFC6BC] hover:border-white/30"
                          }`}
                        >
                          <input
                            type="radio"
                            name="contact_pref"
                            value={c.value}
                            checked={form.contact_pref === c.value}
                            onChange={set("contact_pref")}
                            className="sr-only"
                            data-testid={`moments-pref-${c.value}`}
                          />
                          {c.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="sm:col-span-2 mt-2">
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="moments-submit"
                      className="inline-flex items-center gap-3 rounded-full bg-[#D9A441] px-10 py-4 text-[12px] font-medium uppercase tracking-[0.28em] text-[#181310] transition-colors hover:bg-[#E7B35A] disabled:opacity-50"
                      style={{ boxShadow: "0 0 40px rgba(217,164,65,0.20)" }}
                    >
                      {sending ? "Sending…" : "Send Enquiry"}
                    </button>
                  </div>
                </form>
              </Reveal>
            )}
          </div>
        </div>
        <Footer scrollTo={scrollTo} />
      </main>
    </>
  );
};

export default MomentsPage;
